import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { combineLatest, filter, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { UserActionItem } from '../../models/userActionItem.model';
import {
  GetUserActionItemDetails,
  GetUserActionItems,
  UnsetActiveUserActionItem
} from 'ng2-app/modules/today/state/user-action-items.action';
import { UserActionItemsState } from 'ng2-app/modules/today/state/user-action-items.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { UserActionItemType } from 'ng2-app/modules/today/models/userActionItemType.model';
import { EventBusService } from 'ng2-app/modules/shared/services/event-bus.service';
import { UserActionItemDetailState } from 'ng2-app/modules/today/state/user-action-item-detail.state';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import { notNull } from 'ng2-app/modules/shared/functions/filters';
import { DocumentSaveEvent, KickbackEvent } from 'ng2-app/modules/shared/services/event-types';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TodayBadgeCountsState } from 'ng2-app/modules/today/state/today-badge-counts/today-badge-counts.state';
import { RequestCounts } from 'ng2-app/modules/today/services/today-badge-counts.service';
import { TodayPageState } from 'ng2-app/modules/today/state/today-page-state/today-page.state';
import {
  GetBorrowerPaneWidth,
  SetBorrowerPaneWidth
} from 'ng2-app/modules/today/state/today-page-state/today-page.action';
import { debounce } from 'lodash';

import { isEqual } from 'lodash';

@Component({
  selector: 'app-user-action-items-list-view',
  templateUrl: './user-action-items-list-view.component.html',
  styleUrls: ['./user-action-items-list-view.component.scss']
})
export class UserActionItemsListViewComponent implements OnInit, OnDestroy, AfterViewInit {

  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Select(TodayPageState.borrowerPaneWidth) borrowerPaneWidth$: Observable<number>;
  @Select(UserActionItemDetailState.activeActionItem) activeActionItem$: Observable<UserActionItem>;
  @Select(UserActionItemsState.total) userActionItemsCount$: Observable<number>;
  @Select(UserActionItemsState.loading) loading$: Observable<boolean>;
  @Select(UserActionItemsState.userActionItems) userActionItems$: Observable<UserActionItem[]>;
  @Select(UserActionItemsState.user) user$: Observable<any>;
  @Select(TodayBadgeCountsState.requests) requestBadgeCounts$: Observable<RequestCounts>;
  @Select(LenderUserState.hasPermission('canSeeFdRoleFilter')) canSeeFdRoleFilter$: Observable<boolean>;
  @ViewChild('listContainer') listContainerRef: ElementRef;
  @ViewChild('resizeBar') resizeBarRef: ElementRef;

  @Input() listId: string;
  @Input() completed: boolean = false;

  @HostBinding('style.--user-action-items-list-offset')
  listVertOffset: string;
  @HostBinding('style.--user-action-items-col-width')
  centerPaneWidth: string;
  centerPaneWidthPercent: number;
  resizeBarOffsetInit: boolean = false;
  resizeInitTries: number = 0;

  destroyed$ = new Subject<boolean>();
  userActionItemTypes$: Observable<UserActionItemType[]>;
  requestsBadgeCountsMap: {[key: number]: number};
  showRoleBasedFilters$: Observable<boolean>;

  //filters & list control
  allTypeId: number[] = [0];
  selectedTypeIds: number[] = this.allTypeId;
  selectedTypeLabel: string = 'All';
  sortDirection: string = 'desc';
  page: number = 1;
  showLoadMoreButton$: Observable<boolean>;
  docReviewTypeId = 11;
  subAdminTypeIds = [1,3,6,7,9,10];
  processorTypeIds = [4,7,8,10];

  roleTypes = [
    {
      typeIds: this.subAdminTypeIds,
      label: 'Sub Admin',
    },
    {
      typeIds: this.processorTypeIds,
      label: 'Processor',
    },
  ];

  // defined in UserActionItemsController.php and should match here
  pageSize = 15;


  // is the currently logged-in user the selected user (UserActionItemsState.user)
  actionItemsUserIsAuthUser: boolean = true;

  constructor(
    private store: Store,
    private eventBusService: EventBusService,
    private _snackBar: MatSnackBar,
    private renderer: Renderer2,
  ) { }

  ngOnInit(): void {

    this.userActionItemTypes$ = this.store.select( UserActionItemsState.userActionItemTypesByTab(this.listId) );

    this.user$.pipe(
      filter(notNull),
      takeUntil(this.destroyed$),
    ).subscribe( () => {
      this.resetAndFetch();
    });

    // @ts-ignore
    combineLatest([this.user$, this.lenderUser$])
    .pipe(
      takeUntil(this.destroyed$),
      map(([user, lenderUser]) => {
        return user.id === lenderUser.id;
      })
    ).subscribe( result => {
      this.actionItemsUserIsAuthUser = result;
    });

    this.showLoadMoreButton$ =
    combineLatest([
      this.loading$,
      this.userActionItems$,
      this.userActionItemsCount$,
    ]).pipe(
      takeUntil(this.destroyed$),
      map(([loading, userActionItems, userActionItemsCount]) => {
        return !loading &&
          userActionItemsCount > this.pageSize &&
          userActionItems.length >= this.pageSize &&
          userActionItems.length < userActionItemsCount;
      })
    );

    this.eventBusService.subscribe( this.eventBusService.types.TaskCommentCreatedEvent )
      .pipe(takeUntil(this.destroyed$))
      .subscribe( () => {
        this.getUserActionItems(true);
      });

    this.eventBusService.subscribe( this.eventBusService.types.DocumentSaveEvent )
      .pipe(takeUntil(this.destroyed$))
      .subscribe( (event: DocumentSaveEvent) => {
        const panelClass = `lendio-alert--${event.success ? 'success' : 'error'}`;
        this._snackBar.open( event.message, '', {
          panelClass,
        });
      });

    this.eventBusService.subscribe( this.eventBusService.types.KickbackEvent )
      .pipe(takeUntil(this.destroyed$))
      .subscribe( (event: KickbackEvent) => {
        const panelClass = `lendio-alert--${event.success ? 'success' : 'error'}`;
        this._snackBar.open( event.message, '', {
          panelClass,
          duration: 4000
        });
      });

    this.showRoleBasedFilters$ = combineLatest([this.canSeeFdRoleFilter$, this.listId])
      .pipe(
        takeUntil(this.destroyed$),
        map(([canSeeFdRoleFilter, listId]) => {
          return canSeeFdRoleFilter && listId === 'requests';
        })
      );

    this.store.dispatch( new GetBorrowerPaneWidth() );
    this._updateCenterPaneWidth = debounce(this._updateCenterPaneWidth, 1000);
  }

  ngAfterViewInit(): void {
    this.borrowerPaneWidth$
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe( widthPercent => {
        this.centerPaneWidthPercent = widthPercent;
        this.centerPaneWidth = `${widthPercent}%`;

        if(!this.resizeBarOffsetInit) {
          setTimeout(() => this.setResizeHandleOffset(widthPercent));
        }

      });

    this.requestBadgeCounts$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe( counts => {
      this.requestsBadgeCountsMap = counts?.byType?.reduce( (agg, cur) => {
        agg[cur.typeId] = cur.count;
        return agg;
      }, {});
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  setResizeHandleOffset(borrowerPaneWidth) {
    if(this.resizeInitTries > 10) {
      return;
    }
    if(!this.resizeBarRef || !this.listContainerRef) {
      this.resizeInitTries++;
      setTimeout(() => this.setResizeHandleOffset(borrowerPaneWidth), 1000);
      return;
    }
    const listWidth = this.listContainerRef?.nativeElement?.clientWidth || 0;
    const base = window.innerWidth - listWidth;
    const basePx = base * .5,
      currentPx = base * (borrowerPaneWidth / 100);
    const offset = (basePx - currentPx) * -1;
    this.renderer.setAttribute(this.resizeBarRef.nativeElement, 'style', `transform: translate3d(${offset}px, 0, 0);`);
    this.resizeBarOffsetInit = true;
  }

  handleDrag(dragEvent$) {
    const offset = this.listContainerRef?.nativeElement?.clientWidth || 0;
    const base = window.innerWidth - dragEvent$.pointerPosition.x;
    const val = 100 - (base / (window.innerWidth - offset)) * 100;
    const width = parseFloat(val.toFixed(2));
    this.centerPaneWidthPercent = width;
    this.centerPaneWidth = `${width}%`;
    this._updateCenterPaneWidth();
  }

  _updateCenterPaneWidth() {
    this.store.dispatch( new SetBorrowerPaneWidth(this.centerPaneWidthPercent) );
  }

  selectedTypeChanged({value}): void {
    this.userActionItemTypes$.pipe(take(1)).subscribe( userActionItemTypes => {
      this.selectedTypeIds = [...value];
      this.selectedTypeLabel = value.length > 1
        ? this.roleTypes.filter( roleType => isEqual(roleType.typeIds, value))[0].label || ''
        : userActionItemTypes.filter( type => type.id === value[0] )[0]?.name || 'All';
      this.resetAndFetch();
    });
  }

  selectCompareFn(a, b) {
    return isEqual(a, b);
  }

  resetAndFetch(): void {
    // changing type filters or sorting should reset the total fetch count / pagination
    this.page = 1;
    this.store.dispatch( new UnsetActiveUserActionItem());
    this.getUserActionItems(true);
  }

  loadMore(): void {
    this.page ++;
    this.getUserActionItems(false);
  }

  getUserActionItems(reset: boolean = false) {
    this.userActionItemTypes$.pipe(
      filter(types => !!types?.length),
      take(1),
      map( types => {
        return types.map( type => type.id);
      })
    )
      .subscribe( defaultTypeIds => {
        const types = this.selectedTypeIds && !isEqual(this.selectedTypeIds, [0]) ? this.selectedTypeIds : defaultTypeIds;
        this.store.dispatch(new GetUserActionItems(reset, types, this.sortDirection, this.completed));
      });
  }

  getUserActionItemDetails(actionItemId) {
    this.store.dispatch( new GetUserActionItemDetails(actionItemId));
  }

}
